import { useEffect, useRef, useState } from "react";
import { Command, Save, X } from "lucide-react";
import type { QuoteVisibility, QuoteWorkspaceKind } from "./model";

export function SaveWorkspaceDialog({
  kind,
  initialName,
  initialVisibility,
  busy,
  error,
  inputsConfirmed,
  onCancel,
  onSave,
}: {
  kind: QuoteWorkspaceKind;
  initialName: string;
  initialVisibility: QuoteVisibility;
  busy: boolean;
  error: string | null;
  inputsConfirmed: boolean;
  onCancel(): void;
  onSave(value: { name: string; visibility: QuoteVisibility }): void;
}) {
  const [name, setName] = useState(initialName);
  const [visibility, setVisibility] = useState<QuoteVisibility>(initialVisibility);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  return (
    <Modal title={kind === "quote" ? "Save quote" : "Save preset"} onClose={onCancel}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (trimmed) onSave({ name: trimmed, visibility });
        }}
      >
        <div className="rounded border border-border/70 bg-accent/5 p-3 text-xs text-muted">
          {kind === "quote"
            ? "A quote stores this complete trader sheet: market inputs, selected monthly profile, calculation rules and layout. You can reopen it and save a newer version."
            : "A preset saves the current curve, monthly profile, adders and layout so a trader can apply it to a fresh quote."}
        </div>
        {!inputsConfirmed && (
          <div className="rounded border border-warn-orange/40 bg-warn-orange/5 p-3 text-xs text-warn-orange">
            Market inputs are not trader-confirmed. The workspace can be saved as a draft, but its copied quote ticket remains locked until confirmation.
          </div>
        )}
        <label className="block space-y-1 text-xs">
          <span className="text-muted">Name</span>
          <input
            ref={inputRef}
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            placeholder={kind === "quote" ? "e.g. Nova Cal-27 sell quote" : "e.g. RO physical standard strip"}
            className="h-9 w-full rounded border border-border bg-bg px-2 outline-none focus:border-accent"
          />
        </label>
        <fieldset className="space-y-2">
          <legend className="text-xs text-muted">Visibility</legend>
          <label className="flex cursor-pointer items-start gap-2 rounded border border-border/70 p-2.5 hover:border-accent/50">
            <input type="radio" name="visibility" checked={visibility === "private"} onChange={() => setVisibility("private")} className="mt-0.5 accent-[rgb(var(--accent))]" />
            <span><span className="block text-xs font-medium">Private</span><span className="block text-[10px] text-muted">Only you and company administrators can open it.</span></span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded border border-border/70 p-2.5 hover:border-accent/50">
            <input type="radio" name="visibility" checked={visibility === "company"} onChange={() => setVisibility("company")} className="mt-0.5 accent-[rgb(var(--accent))]" />
            <span><span className="block text-xs font-medium">Company</span><span className="block text-[10px] text-muted">Everyone with EEX access can read or apply it; only its owner or an admin can change it.</span></span>
          </label>
        </fieldset>
        {error && <div className="rounded border border-neg-red/40 bg-neg-red/5 px-3 py-2 text-xs text-neg-red">{error}</div>}
        <div className="flex justify-end gap-2 border-t border-border/70 pt-3">
          <button type="button" onClick={onCancel} disabled={busy} className="h-8 rounded border border-border px-3 text-xs text-muted hover:border-accent/60 hover:text-fg disabled:opacity-40">Cancel</button>
          <button type="submit" disabled={busy || !name.trim()} className="inline-flex h-8 items-center gap-1.5 rounded bg-accent px-3 text-xs font-medium text-white hover:brightness-105 disabled:opacity-40">
            <Save className="h-3.5 w-3.5" />{busy ? "Saving…" : `Save ${kind}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ShortcutDialog({ onClose }: { onClose(): void }) {
  const groups = [
    {
      title: "Select & navigate",
      rows: [
        ["Arrow keys / Tab / Enter", "Move the active cell"],
        ["Shift + click / arrows", "Extend a rectangular selection"],
        ["Ctrl/Cmd + click", "Add or remove a disjoint range"],
        ["Ctrl/Cmd + A", "Select the grid"],
      ],
    },
    {
      title: "Edit",
      rows: [
        ["Type / Enter / double-click", "Edit an input cell"],
        ["Ctrl/Cmd + Enter", "Set the batch value across selected inputs"],
        ["Delete / Backspace", "Clear selected input cells"],
        ["Ctrl/Cmd + D", "Fill selected cells down"],
        ["Ctrl/Cmd + R", "Fill selected cells right"],
        ["Drag fill handle", "Extend a value or pattern"],
      ],
    },
    {
      title: "Clipboard & history",
      rows: [
        ["Ctrl/Cmd + C / X / V", "Copy, cut or paste TSV blocks"],
        ["Ctrl/Cmd + Z", "Undo the last grid transaction"],
        ["Ctrl/Cmd + Shift + Z / Y", "Redo"],
        ["Ctrl/Cmd + S", "Save or update the current workspace"],
        ["?", "Open this shortcut guide"],
      ],
    },
  ];
  return (
    <Modal title="Spreadsheet shortcuts" onClose={onClose} wide>
      <div className="grid gap-5 sm:grid-cols-3">
        {groups.map((group) => (
          <section key={group.title}>
            <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted"><Command className="h-3.5 w-3.5" />{group.title}</h3>
            <div className="space-y-2">
              {group.rows.map(([keys, action]) => (
                <div key={keys} className="rounded border border-border/70 p-2">
                  <kbd className="block text-[11px] font-medium text-fg">{keys}</kbd>
                  <span className="mt-0.5 block text-[10px] text-muted">{action}</span>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
      <div className="mt-5 rounded border border-accent/30 bg-accent/5 p-3 text-xs text-muted">
        The grid accepts normal spreadsheet clipboard blocks. A comma decimal is normalized when it is unambiguous; computed cells stay locked and are skipped during paste.
      </div>
    </Modal>
  );
}

function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: React.ReactNode;
  onClose(): void;
  wide?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? [])].filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    window.requestAnimationFrame(() => {
      if (!dialogRef.current?.contains(document.activeElement)) dialogRef.current?.focus();
    });
    return () => {
      window.removeEventListener("keydown", onKey);
      previouslyFocused?.focus();
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/45 p-4 backdrop-blur-[1px]" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div ref={dialogRef} tabIndex={-1} className={`max-h-[90vh] w-full overflow-y-auto rounded-lg border border-border bg-bg shadow-2xl outline-none ${wide ? "max-w-4xl" : "max-w-lg"}`}>
        <div className="sticky top-0 z-10 flex h-12 items-center border-b border-border/70 bg-bg px-4">
          <h2 className="font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="ml-auto rounded p-1 text-muted hover:bg-accent/10 hover:text-fg" aria-label="Close dialog"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
